import React, { useState, useMemo } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';

// ─── Range presets ─────────────────────────────────────────────────────────
// Each preset returns { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const firstOfMonth = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const lastOfMonth = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1, 0);
  return d.toISOString().slice(0, 10);
};

export const PRESETS = [
  { key: 'today',     label: 'Today',     from: () => today(),       to: () => today() },
  { key: 'yesterday', label: 'Yesterday', from: () => daysAgo(1),    to: () => daysAgo(1) },
  { key: '7d',        label: 'Last 7 days',  from: () => daysAgo(6), to: () => today() },
  { key: '15d',       label: 'Last 15 days', from: () => daysAgo(14), to: () => today() },
  { key: '30d',       label: 'Last 30 days', from: () => daysAgo(29), to: () => today() },
  { key: '90d',       label: 'Last 90 days', from: () => daysAgo(89), to: () => today() },
  { key: 'thisMonth', label: 'This month', from: () => firstOfMonth(0),  to: () => today() },
  { key: 'lastMonth', label: 'Last month', from: () => firstOfMonth(-1), to: () => lastOfMonth(-1) },
];

export const defaultRange = () => {
  const p = PRESETS.find(p => p.key === '30d');
  return { presetKey: '30d', from: p.from(), to: p.to() };
};

// ─── Component ─────────────────────────────────────────────────────────────

const fmtRange = (from, to) => {
  if (!from || !to) return 'Pick a range';
  const f = new Date(from), t = new Date(to);
  const fmt = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  if (from === to) return fmt(f);
  return `${fmt(f)} → ${fmt(t)}`;
};

const dayCount = (from, to) => {
  if (!from || !to) return 0;
  const f = new Date(from), t = new Date(to);
  return Math.round((t - f) / (1000 * 60 * 60 * 24)) + 1;
};

export default function DateRangePicker({ value, onChange }) {
  const { presetKey = 'custom', from = '', to = '' } = value || {};
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(presetKey === 'custom');
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  const days = useMemo(() => dayCount(from, to), [from, to]);

  const applyPreset = (preset) => {
    onChange({ presetKey: preset.key, from: preset.from(), to: preset.to() });
    setCustomMode(false);
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) return;
    if (draftFrom > draftTo) { return; }
    onChange({ presetKey: 'custom', from: draftFrom, to: draftTo });
    setCustomMode(true);
    setOpen(false);
  };

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors"
      >
        <Calendar size={14} className="text-[#f08804]" />
        <span className="font-semibold text-slate-700">{fmtRange(from, to)}</span>
        {days > 0 && (
          <span className="text-[10px] font-semibold text-slate-400">({days} {days === 1 ? 'day' : 'days'})</span>
        )}
        <ChevronDown size={13} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-[320px] bg-white border border-slate-200 rounded-xl shadow-lg z-40 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Date Range
              </span>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            </div>

            {/* Presets */}
            <div className="p-2 grid grid-cols-2 gap-1">
              {PRESETS.map(p => {
                const active = !customMode && presetKey === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => applyPreset(p)}
                    className={`text-left px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                      active
                        ? 'bg-[#FFF4E6] text-[#C45500] font-bold'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Custom */}
            <div className="border-t border-slate-100 p-3 bg-slate-50/30">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Custom range
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={draftFrom}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  className="px-2 py-1.5 border border-slate-200 rounded text-xs flex-1 focus:outline-none focus:border-[#f08804]"
                />
                <span className="text-slate-400 text-xs">→</span>
                <input
                  type="date"
                  value={draftTo}
                  onChange={(e) => setDraftTo(e.target.value)}
                  className="px-2 py-1.5 border border-slate-200 rounded text-xs flex-1 focus:outline-none focus:border-[#f08804]"
                />
              </div>
              <button
                onClick={applyCustom}
                disabled={!draftFrom || !draftTo || draftFrom > draftTo}
                className="w-full mt-2 px-3 py-1.5 bg-[#232F3E] text-white rounded text-xs font-semibold hover:bg-[#37475A] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
